import { MediaResult, SiteSettings, AdPlacementConfig, FAQItem, BlogPost, DownloadLogItem } from '../types';
import { DEFAULT_SITE_SETTINGS, DEFAULT_ADS_CONFIG, DEFAULT_FAQS, INITIAL_BLOG_POSTS } from '../config/siteConfig';
import { saveFirestoreGlobalSettings, fetchFirestoreGlobalSettings } from './firebase';
import {
  fetchPlatformsConfigFromDb,
  fetchGlobalSeoFromDb,
  fetchManagedPagesFromDb,
  fetchRedirectRulesFromDb,
  fetchAdminUsersFromDb,
  fetchSecurityConfigFromDb,
  fetchSmtpConfigFromDb,
  fetchEmailAlertsFromDb,
} from './adminStorage';

const HISTORY_KEY = 'omnifetch_download_history_v1';
const ADS_CACHE_KEY = 'omnifetch_ads_config_cache_v1';

function getInitialAdsConfig(): AdPlacementConfig[] {
  if (typeof window !== 'undefined') {
    try {
      const raw = localStorage.getItem(ADS_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const parsedActive = parsed.filter((a: any) => a.enabled && (a.code || a.adsterraZoneKey)).length;
          const defaultActive = DEFAULT_ADS_CONFIG.filter((a) => a.enabled && (a.code || a.adsterraZoneKey)).length;
          if (parsedActive >= defaultActive) {
            return parsed;
          }
          return DEFAULT_ADS_CONFIG.map((def) => {
            const found = parsed.find((p: any) => p.slot === def.slot || p.id === def.id);
            if (found && (found.code || found.adsterraZoneKey)) {
              return { ...def, ...found };
            }
            return def;
          });
        }
      }
    } catch {}
  }
  return DEFAULT_ADS_CONFIG;
}

// Internal memory caches
let cachedSettings: SiteSettings = DEFAULT_SITE_SETTINGS;
let cachedAdsConfig: AdPlacementConfig[] = getInitialAdsConfig();
let cachedFaqs: FAQItem[] = DEFAULT_FAQS;
let cachedBlogs: BlogPost[] = INITIAL_BLOG_POSTS;
let currentSyncVersion = 0;

export function getDownloadHistory(): MediaResult[] {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem(HISTORY_KEY) : null;
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveToHistory(item: MediaResult): void {
  try {
    const existing = getDownloadHistory();
    const filtered = existing.filter((x) => x.id !== item.id && x.originalUrl !== item.originalUrl);
    const updated = [item, ...filtered].slice(0, 30);
    if (typeof window !== 'undefined') {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
    }

    // Post to PostgreSQL download logging API asynchronously
    fetch('/api/trending', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: item.originalUrl,
        title: item.title,
        platform: item.platformName,
        thumbnail: item.thumbnail,
        quality: item.formats?.[0]?.quality || 'HD No Watermark',
      }),
    }).catch(() => {});

    logDownloadEvent(item);
  } catch (e) {
    console.error('Error saving history:', e);
  }
}

export function clearDownloadHistory(): void {
  try {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(HISTORY_KEY);
    }
  } catch (e) {
    console.error('Error clearing history:', e);
  }
}

// --- Site Settings ---
export function getSiteSettings(): SiteSettings {
  return cachedSettings;
}

export async function fetchSiteSettingsFromDb(): Promise<SiteSettings> {
  try {
    const res = await fetch('/api/settings');
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.settings) {
        cachedSettings = { ...DEFAULT_SITE_SETTINGS, ...data.settings };
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('omnifetch_settings_updated', { detail: cachedSettings }));
        }
        return cachedSettings;
      }
    }
  } catch (e) {
    console.error('Error fetching site settings from DB:', e);
  }
  return cachedSettings;
}

export async function saveSiteSettingsToDb(settings: Partial<SiteSettings>): Promise<SiteSettings> {
  // Mirror to Firestore so client & remote sync stay 100% identical
  try {
    await saveFirestoreGlobalSettings(settings);
  } catch (fsErr) {
    console.warn('Notice: Mirroring settings to Firestore warning:', fsErr);
  }

  let data: any = null;
  try {
    const res = await fetch('/api/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    });
    const text = await res.text();
    try {
      data = JSON.parse(text);
    } catch {
      console.warn('[Storage] API /api/settings returned non-JSON response:', text.slice(0, 80));
    }
  } catch (netErr) {
    console.warn('[Storage] Network error posting to /api/settings:', netErr);
  }

  cachedSettings = { ...DEFAULT_SITE_SETTINGS, ...cachedSettings, ...(data?.settings || settings) };
  if (data?.syncVersion) currentSyncVersion = data.syncVersion;

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('omnifetch_site_settings', JSON.stringify(cachedSettings));
    } catch {}
    window.dispatchEvent(new CustomEvent('omnifetch_settings_updated', { detail: cachedSettings }));
  }
  return cachedSettings;
}

export function saveSiteSettings(settings: Partial<SiteSettings>): SiteSettings {
  cachedSettings = { ...cachedSettings, ...settings };
  saveSiteSettingsToDb(settings).catch((e) => console.error('Background save error:', e));
  return cachedSettings;
}

// --- Base64 Transport Helpers for WAF Safety ---
export function encodeUtf8ToBase64(str: string): string {
  if (!str) return '';
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(str, 'utf-8').toString('base64');
  }
  return str;
}

export function decodeBase64ToUtf8(b64: string): string {
  if (!b64) return '';
  if (typeof window !== 'undefined' && typeof window.atob === 'function') {
    try {
      const binary = window.atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new TextDecoder('utf-8').decode(bytes);
    } catch {
      return b64;
    }
  }
  if (typeof Buffer !== 'undefined') {
    try {
      return Buffer.from(b64, 'base64').toString('utf-8');
    } catch {
      return b64;
    }
  }
  return b64;
}

// --- Ads Management ---
export function getAdsConfig(): AdPlacementConfig[] {
  return cachedAdsConfig;
}

export async function fetchAdsConfigFromDb(): Promise<AdPlacementConfig[]> {
  let combinedAds: AdPlacementConfig[] = [...DEFAULT_ADS_CONFIG];

  // 1. Fetch from server API
  try {
    const res = await fetch('/api/ads');
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.ads && Array.isArray(data.ads) && data.ads.length > 0) {
        data.ads.forEach((incoming: AdPlacementConfig) => {
          let cleanCode = incoming.code || '';
          if (typeof cleanCode === 'string' && cleanCode.startsWith('base64:')) {
            cleanCode = decodeBase64ToUtf8(cleanCode.slice(7));
          }
          const hasContent = cleanCode.trim().length > 0 || Boolean(incoming.adsterraZoneKey);
          const normalized: AdPlacementConfig = {
            ...incoming,
            code: cleanCode,
            enabled: hasContent ? true : incoming.enabled,
          };
          const idx = combinedAds.findIndex((c) => c.slot === normalized.slot || c.id === normalized.id);
          if (idx !== -1) {
            if (hasContent) {
              combinedAds[idx] = { ...combinedAds[idx], ...normalized };
            }
          } else {
            combinedAds.push(normalized);
          }
        });
      }
    }
  } catch (e) {
    console.warn('[Storage] Notice fetching ads from /api/ads, trying Firestore backup:', e);
  }

  // 2. Firestore remote cloud database
  try {
    const fsSettings = await fetchFirestoreGlobalSettings();
    if (fsSettings && Array.isArray(fsSettings.adsConfig) && fsSettings.adsConfig.length > 0) {
      fsSettings.adsConfig.forEach((fsAd: any) => {
        const hasContent = Boolean(fsAd.code && fsAd.code.trim().length > 0) || Boolean(fsAd.adsterraZoneKey);
        const idx = combinedAds.findIndex((c) => c.slot === fsAd.slot || c.id === fsAd.id);
        if (idx !== -1) {
          if (hasContent) {
            combinedAds[idx] = { ...combinedAds[idx], ...fsAd, enabled: true };
          }
        } else {
          combinedAds.push(fsAd);
        }
      });
    }
  } catch (fsErr) {
    console.warn('[Storage] Notice fetching ads from Firestore backup:', fsErr);
  }

  cachedAdsConfig = combinedAds;
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(ADS_CACHE_KEY, JSON.stringify(cachedAdsConfig));
    } catch {}
    window.dispatchEvent(new CustomEvent('omnifetch_ads_updated', { detail: cachedAdsConfig }));
  }
  return cachedAdsConfig;
}

export async function saveAdsConfigToDb(ads: AdPlacementConfig[]): Promise<AdPlacementConfig[]> {
  // 1. Prepare WAF-safe transport payload by encoding code strings in Base64
  const transportAds = ads.map((ad) => {
    let transportCode = ad.code || '';
    if (typeof transportCode === 'string' && transportCode.length > 0 && !transportCode.startsWith('base64:')) {
      transportCode = 'base64:' + encodeUtf8ToBase64(transportCode);
    }
    return {
      ...ad,
      code: transportCode,
    };
  });

  // 2. Mirror synchronously to Firestore so client & remote sync stay 100% identical
  try {
    await saveFirestoreGlobalSettings({ adsConfig: ads });
  } catch (fsErr) {
    console.warn('Notice: Mirroring adsConfig to Firestore warning:', fsErr);
  }

  // 3. Post WAF-safe payload to server API safely
  let data: any = null;
  try {
    const res = await fetch('/api/ads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ads: transportAds }),
    });
    const text = await res.text();
    try {
      data = JSON.parse(text);
    } catch {
      console.warn('[Storage] API /api/ads returned non-JSON response:', text.slice(0, 80));
    }
  } catch (netErr) {
    console.warn('[Storage] Network error posting to /api/ads:', netErr);
  }

  // 4. Decode returned ads for local in-memory and event caching
  const returnedAds: AdPlacementConfig[] = Array.isArray(data?.ads)
    ? data.ads.map((ad: AdPlacementConfig) => {
        let cleanCode = ad.code || '';
        if (typeof cleanCode === 'string' && cleanCode.startsWith('base64:')) {
          cleanCode = decodeBase64ToUtf8(cleanCode.slice(7));
        }
        return {
          ...ad,
          code: cleanCode,
        };
      })
    : ads;

  cachedAdsConfig = returnedAds;
  if (data?.syncVersion) currentSyncVersion = data.syncVersion;
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(ADS_CACHE_KEY, JSON.stringify(cachedAdsConfig));
    } catch {}
    window.dispatchEvent(new CustomEvent('omnifetch_ads_updated', { detail: cachedAdsConfig }));
  }
  return cachedAdsConfig;
}

export async function saveAdsConfig(ads: AdPlacementConfig[]): Promise<AdPlacementConfig[]> {
  cachedAdsConfig = ads;
  return await saveAdsConfigToDb(ads);
}

// --- FAQs & Blogs ---
export function getFaqsConfig(): FAQItem[] {
  return cachedFaqs;
}

export async function fetchFaqsConfigFromDb(): Promise<FAQItem[]> {
  try {
    const res = await fetch('/api/faqs');
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.faqs)) {
        cachedFaqs = data.faqs.length > 0 ? data.faqs : DEFAULT_FAQS;
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('omnifetch_faqs_updated', { detail: cachedFaqs }));
        }
        return cachedFaqs;
      }
    }
  } catch (e) {
    console.error('Error fetching FAQs from DB:', e);
  }
  return cachedFaqs;
}

export async function saveFaqsConfigToDb(faqs: FAQItem[]): Promise<FAQItem[]> {
  const res = await fetch('/api/faqs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ faqs }),
  });
  if (!res.ok) {
    throw new Error(`Server returned status ${res.status}`);
  }
  const data = await res.json();
  if (!data.success) {
    throw new Error(data.message || data.error || 'Failed to save FAQs');
  }
  cachedFaqs = Array.isArray(data.faqs) ? data.faqs : faqs;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('omnifetch_faqs_updated', { detail: cachedFaqs }));
  }
  return cachedFaqs;
}

export async function saveFaqsConfig(faqs: FAQItem[]): Promise<FAQItem[]> {
  cachedFaqs = faqs;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('omnifetch_faqs_updated', { detail: faqs }));
  }
  return saveFaqsConfigToDb(faqs);
}

export function getBlogsConfig(): BlogPost[] {
  return cachedBlogs;
}

export async function fetchBlogsConfigFromDb(): Promise<BlogPost[]> {
  try {
    const res = await fetch('/api/blogs');
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.blogs)) {
        cachedBlogs = data.blogs.length > 0 ? data.blogs : INITIAL_BLOG_POSTS;
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('omnifetch_blogs_updated', { detail: cachedBlogs }));
        }
        return cachedBlogs;
      }
    }
  } catch (e) {
    console.error('Error fetching Blogs from DB:', e);
  }
  return cachedBlogs;
}

export async function saveBlogsConfigToDb(blogs: BlogPost[]): Promise<BlogPost[]> {
  const res = await fetch('/api/blogs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blogs }),
  });
  if (!res.ok) {
    throw new Error(`Server returned status ${res.status}`);
  }
  const data = await res.json();
  if (!data.success) {
    throw new Error(data.message || data.error || 'Failed to save blogs');
  }
  cachedBlogs = Array.isArray(data.blogs) ? data.blogs : blogs;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('omnifetch_blogs_updated', { detail: cachedBlogs }));
  }
  return cachedBlogs;
}

export async function saveBlogsConfig(blogs: BlogPost[]): Promise<BlogPost[]> {
  cachedBlogs = blogs;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('omnifetch_blogs_updated', { detail: blogs }));
  }
  return saveBlogsConfigToDb(blogs);
}

function logDownloadEvent(result: MediaResult): void {
  // Download logs are persisted to PostgreSQL /api/trending
}

export function getAdminDownloadLogs(): DownloadLogItem[] {
  return [];
}

// --- Realtime Synchronization Poller ---
let realtimeSyncInitialized = false;

export function initRealtimeSyncLoop(): void {
  if (realtimeSyncInitialized || typeof window === 'undefined') return;
  realtimeSyncInitialized = true;

  const isAdminRoute = () => {
    if (typeof window === 'undefined') return false;
    const p = window.location.pathname.toLowerCase();
    return p.includes('/admin') || p.includes('/admin-download') || p.includes('/admin-login');
  };

  // Selective data fetcher
  const syncPublicModules = () => {
    fetchSiteSettingsFromDb();
    fetchAdsConfigFromDb();
  };

  const syncAdminModules = () => {
    fetchFaqsConfigFromDb();
    fetchBlogsConfigFromDb();
    fetchPlatformsConfigFromDb();
    fetchGlobalSeoFromDb();
    fetchManagedPagesFromDb();
    fetchRedirectRulesFromDb();
    fetchAdminUsersFromDb();
    fetchSecurityConfigFromDb();
    fetchSmtpConfigFromDb();
    fetchEmailAlertsFromDb();
  };

  // Initial load
  syncPublicModules();
  if (isAdminRoute()) {
    syncAdminModules();
  }

  // Poll sync version: 5s on admin dashboard, 45s for regular visitors (adaptive backoff)
  let lastCheck = Date.now();
  setInterval(async () => {
    const isAdm = isAdminRoute();
    const minInterval = isAdm ? 5000 : 45000;
    if (Date.now() - lastCheck < minInterval) return;
    lastCheck = Date.now();

    try {
      const res = await fetch('/api/sync/version');
      if (res.ok) {
        const data = await res.json();
        if (data.success && typeof data.version === 'number') {
          if (currentSyncVersion === 0) {
            currentSyncVersion = data.version;
          } else if (data.version > currentSyncVersion) {
            currentSyncVersion = data.version;
            syncPublicModules();
            if (isAdm) {
              syncAdminModules();
            }
          }
        }
      }
    } catch {}
  }, 5000);
}
